import {Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {
    AbstractControl,
    AsyncValidatorFn,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';

import {BehaviorSubject, combineLatest, Observable, of, shareReplay, Subject, Subscription, zip} from 'rxjs';
import {debounceTime, filter, map, startWith, switchMap, take, tap} from 'rxjs/operators';

import {Message, SelectItem} from 'primeng/api';
import { MessageService } from '@shared/services/message-service';
import {Dropdown} from 'primeng/dropdown';

import {DateTime} from 'luxon';

import {environment} from '@environment';

import {ActivityBlockingService} from '@shared/services/activity-blocking.service';
import {CarrierService} from '@shared/services/carrier.service';
import {CustomerService} from '@shared/services/customer.service';
import {CustomValidators} from '@shared/helpers/custom-validators';

import {Carrier} from '@shared/models/carrier';
import {Customer, CustomerAssignmentSummary} from '@shared/models/customer';
import {CustomerSite} from '@shared/models/customer-site';
import {OutboundLoadExceptionType} from '@shared/models/OutboundLoadExceptionType';
import {Product} from '@shared/models/product';
import {SubinventoryType} from '@shared/models/subinventory';

import {AutoSaveService} from '@shared/services/auto-save.service';
import {BolService} from '../../../services/bol.service';
import {OutboundLoadService} from '../../../services/outbound-load.service';
import {ProductService} from '../../../services/product.service';

import {OutboundLoad, OutboundLoadException, OutboundLoadLineItem} from '../../../models/outbound-load';
import {OutboundLoadStatus} from '../../../models/outbound-load-status';
import {ShiftContextService} from '../../../services/shift-context.service';
import {ShiftContext} from '../../../models/shift-context';
import { timeFormat } from '@shared/helpers/timeFormat';
import { fromFormControl } from '@shared/helpers/from-form-control';
import { WorkedShiftsService } from '@shared/services/worked-shifts.service';
import { WorkedShift } from '@shared/models/worked-shift';
import { SiteConfig } from '@shared/models/site-config';
import { toTimestamp } from '../../../helpers/to-timestamp';

export const CUST_REF_PATTERN: RegExp = /^[A-Za-z0-9]*$/;

function spacesValidator(control:AbstractControl): ValidationErrors | null {
    const val: string = control.value ?? '';
    return val.trim() !== val ? { containsSpaces: true } : null;
}

@Component({
    selector: 'app-outbound-loads-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss']
})
export class OutboundLoadsFormComponent implements OnInit, OnDestroy {
    pageName = 'OutboundLoad';
    formDataName = 'OutboundLoadForm';

    @ViewChild('poolerSelect', { static: false }) poolerSelect: Dropdown;
    @ViewChild('productSelect', { static: false }) productSelect: Dropdown;
    @ViewChildren('products') productsDropdown:  QueryList<Dropdown>;

    outboundLoadForm = new FormGroup({
        loadType: new FormControl<OutboundLoadStatus>(OutboundLoadStatus.Loaded),
        siteSubinventoryId: new FormControl<number>(null, Validators.required),
        trailerId: new FormControl<number>(null, Validators.required),
        sealNumber: new FormControl<string>(''),
        loadDate: new FormControl<Date>(null, Validators.required),
        loadTime: new FormControl<string>('', Validators.required),
        loadShiftId: new FormControl<number>(null, Validators.required),
        carrierId: new FormControl<number>(null, Validators.required),
        carrierName: new FormControl<string>(''),
        customerId: new FormControl<number>(null, Validators.required),
        poolerId: new FormControl<number>(null, Validators.required),
        shipToLocation: new FormControl<number>(null),
        customerReferenceNumber: new FormControl<string>(null, Validators.pattern(CUST_REF_PATTERN)),
        altReferenceNumber: new FormControl<string>(null, [ spacesValidator, Validators.pattern(CUST_REF_PATTERN) ]),
        propakBOLNum: new FormControl<string>('', Validators.required),
        isReuse: new FormControl<boolean>(false),
        customerNeededDate: new FormControl<Date>(null),
        customerNeededTime: new FormControl<string>(''),
        scheduledPickupDate: new FormControl<Date>(null),
        scheduledPickupTime: new FormControl<string>(''),
        dateShipped: new FormControl<Date>(null),
        timeShipped: new FormControl<string>(''),
        shippedShiftId: new FormControl<number>(null),
        isRejected: new FormControl<boolean>(false),
        rejectionReason: new FormControl<string>(''),
        comments: new FormControl<string>(''),
        resendNotification: new FormControl<boolean>(false)
    });

    outboundLoadLineForm = new FormGroup({
        productId: new FormControl<number>(null, {validators: Validators.required, updateOn: 'submit'}),
        quantity: new FormControl<number>(0, Validators.required),
        isScanned: new FormControl<boolean>(false),
        isUnderload: new FormControl<boolean>(false)
    });

    exceptionForm = new FormGroup({
        outboundLoadExceptionType: new FormControl<OutboundLoadExceptionType>(null, Validators.required),
        description: new FormControl<string>('', Validators.required)
    });

    showCarrierName: boolean = false;
    displayAddShipToDialog: boolean;
    displayDialog: boolean;
    existingLoadId: number;
    saving: boolean;
    entryFocus: boolean;

    loadTypeOptions: SelectItem[] = [
        { label: 'Planned', value: OutboundLoadStatus.Planned },
        { label: 'Loaded', value: OutboundLoadStatus.Loaded },
        { label: 'Shipped', value:  OutboundLoadStatus.Shipped }
    ];

    recentShipTos: { [key: string]: number[] };
    recentShipToIds: number[] = [];

    context: ShiftContext;

    carriers: Carrier[] = [];
    products: Product[] = [];
    availableTrailers: string[] = [];

    customerAssignments = new BehaviorSubject<CustomerAssignmentSummary>({ customers: [], assignments: [], poolerRecentShipTos: {} });

    customers = combineLatest([
        this.customerAssignments,
        this.outboundLoadForm.controls.siteSubinventoryId.valueChanges
    ]).pipe(map(([customerAssignments, siteSubinventoryId]) => {
        const ids = customerAssignments.assignments
            .filter(a => a.siteSubinventoryId === siteSubinventoryId)
            .map(a => a.customerId);
        this.recentShipTos = customerAssignments.poolerRecentShipTos;
        return customerAssignments.customers.filter(c => c.isActive && c.isPrimary && ids.includes(c.id));
    }), shareReplay(1));

    poolers = combineLatest([
        this.customerAssignments,
        fromFormControl(this.outboundLoadForm.controls.siteSubinventoryId),
        fromFormControl(this.outboundLoadForm.controls.customerId)
    ]).pipe(map(([customerAssignments, siteSubinventoryId, customerId]) => {
        const ids = customerAssignments.assignments
            .filter(a => a.siteSubinventoryId === siteSubinventoryId && a.customerId === customerId)
            .map(a => a.poolerId);

        return customerAssignments.customers.filter(c => c.isActive && c.isPooler && ids.includes(c.id));
    }));


    siteSubinventories: SelectItem[] = [];
    carrierSelects: SelectItem[] = [];

    selectedPooler = combineLatest([
        this.poolers,
        fromFormControl(this.outboundLoadForm.controls.poolerId)
    ]).pipe(map(([poolers, selectedId]) => {
        const selected = poolers.find(p => p.id === selectedId);
        if (selected) {
            this.poolerCustRefRequired = selected.isCustomerRefRequired;
            this.handleCustomerReferenceRequired();
        }
        if (!!this.recentShipTos?.[selectedId])
            this.recentShipToIds = this.recentShipTos?.[selectedId];
        return selected;
    }));

    poolerName = this.selectedPooler.pipe(map(p => p?.accountName));

    poolerSites = this.selectedPooler.pipe(map(p => p?.customerSites ?? []));

    selectedSubinventoryId = fromFormControl(this.outboundLoadForm.controls.siteSubinventoryId);

    selectedCarrier = fromFormControl(this.outboundLoadForm.controls.carrierId)
        .pipe(map(carrierId => this.carriers.find(c => c.id === carrierId)));


    customersSelects = combineLatest([
        this.customers.pipe(startWith<Customer[]>([])),
        fromFormControl(this.outboundLoadForm.controls.customerId)
    ]).pipe(
        map(([customers, custId]) => {
            return [
                ...((!custId || customers?.find(c => c.id === custId)) ? [] : [{ value: custId, label: '<Unknown Customer>' }]),
                ...customers.map(c => ({ value: c.id, label: c.accountName }))
            ];
        }));

    poolersSelects = combineLatest([
        this.poolers.pipe(startWith<Customer[]>([])),
        fromFormControl(this.outboundLoadForm.controls.poolerId)
    ]).pipe(
        map(([poolers, poolerId]) => {
            return [
                ...((!poolerId || poolers?.find(c => c.id === poolerId)) ? [] : [{ value: poolerId, label: '<Unknown Pooler>' }]),
                ...poolers.map(c => ({ value: c.id, label: c.accountName }))
            ];
        }));


    poolerSitesSelects = combineLatest([
        this.poolerSites.pipe(startWith([])),
        this.customerAssignments,
        fromFormControl(this.outboundLoadForm.controls.customerId),
        fromFormControl(this.outboundLoadForm.controls.poolerId),
        fromFormControl(this.outboundLoadForm.controls.shipToLocation)
    ]).pipe(map(([sites, assignments, customerId, poolerId, poolerSiteId]) => {
        const poolerAssignments = assignments?.assignments
            .filter(a => a.customerId === customerId && a.poolerId === poolerId) ?? [];

        return [
            { value: null, label: '-- New Site --', styleClass: 'add' },
            ...sites
                .filter(s => poolerAssignments.some(a => a.poolerSiteId === s.id || !a.poolerSiteId))
                .map(s => ({ site: s, isRecent: this.recentShipToIds.includes(s.id) }))
                .sort((a, b) => a.isRecent ? -1 : 1)
                .map((s, index) => ({
                    value: s.site.id,
                    label: s.site.chepGlobalLocationId ? `${s.site.chepGlobalLocationId} - ${s.site.partySiteName}` : s.site.partySiteName,
                    styleClass: {
                        'recent': index === 0 && this.recentShipToIds.length > 0,
                        'all': index === this.recentShipToIds.length,
                        'text-strikethrough': s.site.activeFlag === false
                    }
            })),
            ...((!poolerSiteId || sites?.find(c => c.id === poolerSiteId)) ? [] : [{ value: poolerSiteId, label: '<Unknown Site>' }])
        ];
    }));

    poolerCustRefRequired: boolean = false;
    stockTransfer: boolean = false;

    productSelects = combineLatest([
        fromFormControl(this.outboundLoadForm.controls.siteSubinventoryId),
        fromFormControl(this.outboundLoadForm.controls.customerId),
        fromFormControl(this.outboundLoadForm.controls.poolerId),
        fromFormControl(this.outboundLoadForm.controls.shipToLocation),
        this.productService.CurrentProducts,
        this.customerAssignments
    ]).pipe(
        debounceTime(500),
        map(([ subinvId, customerId, poolerId, poolerSiteId, products, assignments ]) => {
            const productIds = assignments.assignments
                .filter(a => a.customerId == customerId && a.siteSubinventoryId == subinvId
                    && (a.poolerId == poolerId || !a.poolerId)
                    && (a.poolerSiteId == poolerSiteId || !a.poolerSiteId))
                .flatMap(a => a.productIds);

            return [
                { value: null, label: '-- Select Product --'},
                ...products.filter(p => productIds.includes(p.id)).map(p => ({ value: p.id, label: p.label }))
            ];
        }));

    eDefaultValuefields: { outboundLoadExceptionType: null, description: ''};
    ExceptionTypes = OutboundLoadExceptionType;
    outboundLoadExceptions: OutboundLoadException[];
    documents: string[];

    customerReferenceRequired = false;
    newIGPS: boolean;

    cfGlidRequired: boolean;
    invalidProduct: boolean = false;

    trailerEligibleFor: Observable<'loading' | 'departing'> = fromFormControl(this.outboundLoadForm.controls.loadType)
        .pipe(map(status => {
            if (status === OutboundLoadStatus.Shipped)
                return 'departing';
            else
                return 'loading';
        }));

    isShipped = fromFormControl(this.outboundLoadForm.controls.loadType)
        .pipe(map(status => status === OutboundLoadStatus.Shipped));

    loadedNotificationTS: DateTime | null;

    olDefaultValuefields: { [key: string]: any };

    closedWarning: string = '';

    autoSaveEnabled: boolean = false;

    readonly _outboundLoadLineItemNotCreatedMsg: Message = {
        severity: 'warn',
        summary: 'Could not Create Outbound Load Line Item',
        detail: 'Fields are required.'
    };

    readonly _outboundLoadLineItemCreatedMsg: Message = {
        severity: 'success',
        summary: 'Outbound Load Line Item Created',
        detail: ''
    };

    readonly _outboundLoadLineItemDeletedMsg: Message = {
        severity: 'warn',
        summary: 'Outbound Load Line Item Deleted',
        detail: ''
    };

    readonly _exceptionItemNotCreatedMsg: Message = {
        severity: 'warn',
        summary: 'Could not Create Exception Item',
        detail: 'Fields are required.'
    };

    readonly _exceptionItemCreatedMsg: Message = {
        severity: 'success',
        summary: 'Exception Item Created',
        detail: ''
    };
    readonly _exceptionItemDeletedMsg: Message = {
        severity: 'warn',
        summary: 'Exception Item Deleted',
        detail: ''
    };

    showAltRefNum: boolean = false;

    // begin outbound load line item grid variables
    outboundLoadLineItem: OutboundLoadLineItem = { id: null, productId: null, quantity: 0, isScanned: false, isUnderload: false };
    newOutboundLoadLineItem: boolean;
    outboundLoadLineItems: OutboundLoadLineItem[] = [];
    clonedOutboundLoadItems: { [s: string]: OutboundLoadLineItem } = {};
    // end outbound load line item grid variables

    // begin exception item grid variables
    exceptionItem: OutboundLoadException = { id: null, description: '' };
    newExceptionItem: boolean;
    exceptionItems: OutboundLoadException[] = [];
    clonedExceptionItems: { [s: string]: OutboundLoadException } = {};
    // end exception item grid variables

    siteId: string;
    siteTimezone: string = environment.fallbackTimeZone;

    subscriptions: Subscription[];
    startupSubscription: Subscription;

    constructor(private router: Router, private route: ActivatedRoute, private outboundLoadService: OutboundLoadService,
                private carrierService: CarrierService, private productService: ProductService,
                private messageService: MessageService, private autoSaveService: AutoSaveService,
                private shiftContextService: ShiftContextService, private activityService: ActivityBlockingService,
                private customerService: CustomerService, private bolService: BolService,
                private workedShiftService: WorkedShiftsService) {
    }

    handleLoadTypeChange(type: OutboundLoadStatus): void {
        this.outboundLoadForm.controls.shippedShiftId.clearValidators();
        this.outboundLoadForm.controls.dateShipped.clearValidators();
        this.outboundLoadForm.controls.timeShipped.clearValidators();
        this.outboundLoadForm.controls.shipToLocation.clearValidators();
        this.outboundLoadForm.controls.loadShiftId.clearValidators();
        this.outboundLoadForm.clearAsyncValidators();

        if (type === OutboundLoadStatus.Loaded) {
            this.outboundLoadForm.controls.loadShiftId.setValidators(Validators.required);
            this.outboundLoadForm.controls.shipToLocation.setValidators(CustomValidators.requiredIf(_ => this.outboundLoadLineItems
                    .map(li => this.products.find(p => p.id === li.productId))
                    .some(p => p?.customer === 'WW')));
        }
        else if (type ===  OutboundLoadStatus.Shipped) {
            this.outboundLoadForm.controls.loadShiftId.setValidators([Validators.required]);
            this.outboundLoadForm.controls.shippedShiftId.setValidators([Validators.required]);
            this.outboundLoadForm.controls.dateShipped.setValidators(Validators.required);
            this.outboundLoadForm.controls.timeShipped.setValidators(Validators.required);
            this.outboundLoadForm.controls.shipToLocation.setValidators([
                Validators.required,
                this.addressValidator()
            ]);
            this.outboundLoadForm.setAsyncValidators([this.shiftSequenceValidator()]);
        }

        this.outboundLoadForm.controls.loadShiftId.updateValueAndValidity();
        this.outboundLoadForm.controls.shipToLocation.updateValueAndValidity();
        this.outboundLoadForm.controls.shippedShiftId.updateValueAndValidity();
        this.outboundLoadForm.controls.dateShipped.updateValueAndValidity();
        this.outboundLoadForm.controls.timeShipped.updateValueAndValidity();

        this.handleCustomerReferenceRequired();
        this.handleWorkedShiftDateFields(type);
    }

    handleCustomerReferenceRequired(): void {
        const customerId = this.outboundLoadForm.controls.customerId.value;
        this.outboundLoadForm.controls.customerReferenceNumber.clearValidators();
        const customerUpdated = this.customerAssignments.value.customers.find(c => c.id === customerId);

        if (customerUpdated) {
            this.customerReferenceRequired =
                (customerUpdated.isCustomerRefRequired ||
                this.poolerCustRefRequired) &&
                this.outboundLoadForm.controls.loadType.value ===  OutboundLoadStatus.Shipped;

            if (this.customerReferenceRequired)
                this.outboundLoadForm.controls.customerReferenceNumber.setValidators(Validators.required);
        }

        this.outboundLoadForm.controls.customerReferenceNumber.addValidators([spacesValidator, Validators.pattern(CUST_REF_PATTERN)]);

        this.outboundLoadForm.controls.customerReferenceNumber.updateValueAndValidity();
    }

    createLineItemProductSelects(row: OutboundLoadLineItem): Observable<SelectItem<number | null>[]> {
        return combineLatest([
            this.productSelects.pipe(startWith([])),
            this.productService.CurrentProducts.pipe(filter(p => !!p), startWith([]))
        ]).pipe(map(([selects, products]) => {
            return selects.some(p => p.value === row.productId)
                ? selects
                : [
                    ...selects,
                    {
                        value: row.productId,
                        label: products.find(product => product.id === row.productId)?.label ?? '-- Unknown Product --'
                    }
                ];
        }));
    }

    handleWorkedShiftDateFields(type: OutboundLoadStatus): void {
        if (this.context)
            switch (type) {
                case OutboundLoadStatus.Planned:
                case OutboundLoadStatus.Canceled:
                    this.outboundLoadForm.controls.loadShiftId.setValue(null);
                    this.outboundLoadForm.controls.shippedShiftId.setValue(null);
                    return;
                case OutboundLoadStatus.Loaded:
                    if (this.outboundLoadForm.controls.loadShiftId.value == null)
                        this.outboundLoadForm.controls.loadShiftId.setValue(this.context.id);

                    if (this.outboundLoadForm.controls.loadDate.value == null)
                        this.outboundLoadForm.controls.loadDate.setValue(this.context.date.toJSDate());
                    return;
                case OutboundLoadStatus.Shipped:
                    if (this.outboundLoadForm.controls.shippedShiftId.value == null)
                        this.outboundLoadForm.controls.shippedShiftId.setValue(this.context.id);

                    if (this.outboundLoadForm.controls.dateShipped.value == null)
                        this.outboundLoadForm.controls.dateShipped.setValue(this.context.date.toJSDate());
                    return;
            }
    }


    addressValidator(): ValidatorFn {
        return (control: AbstractControl): {[key: string]: any} | null => {
            if (!control.value)
                return null;
            const customer = this.customerAssignments.value.customers.find(c => c.id === this.outboundLoadForm.controls.customerId.value);
            if (customer?.isShipToAddressRequired) {
                const customerSite = this.getPoolerSite();
                const hasAddress = customerSite?.address1?.length > 0;
                return hasAddress ? null : {noAddress: {value: control.value}};
            }
            return null;
        };
    }

    shiftSequenceValidator(): AsyncValidatorFn {
        return (form: FormGroup): Promise<ValidationErrors> => {
            if (!form.controls.loadShiftId.value || !form.controls.shippedShiftId.value)
                return Promise.resolve(null);

            return new Promise(resolve => {
                combineLatest([
                    this.workedShiftService.PeekById(form.controls.loadShiftId.value),
                    this.workedShiftService.PeekById(form.controls.shippedShiftId.value)
                ])
                    .pipe(take(1))
                    .subscribe(([loadedShift, shippedShift]) => {
                        const isValid = shippedShift && loadedShift
                            ? loadedShift.date < shippedShift.date
                                || (loadedShift.date.equals(shippedShift.date) && loadedShift?.shift.number <= shippedShift?.shift.number)
                            : true;

                        resolve(isValid ? null :  { shiftSequence: true });
                    });
            });
        };
    }

    getPoolerSite(): CustomerSite {
        const cust: Customer = this.customerAssignments.value.customers
            .find(c => c.id === this.outboundLoadForm.controls.poolerId.value);
        return cust ? cust.customerSites?.find(cs => cs.id === this.outboundLoadForm.controls.shipToLocation.value) : null;
    }

    ngOnInit(): void {
        this.autoSaveEnabled = false;
        this.newIGPS = false;
        // this.exceptions  = this.OutboundExceptions.filter(oef => oef).map(oe => ({ value: oe.valueOf(), label: oe.toString()}));
        this.handleLoadTypeChange( OutboundLoadStatus.Shipped);
        this.outboundLoadForm.setValidators((_form: typeof this.outboundLoadForm): ValidationErrors => {
            if (this.outboundLoadForm.controls.loadType.value === OutboundLoadStatus.Planned
                || (this.outboundLoadLineItems && this.outboundLoadLineItems.length > 0))
                return null;
            else
                return {
                    lineItemsExist: {
                        valid: false
                    }
                };
        });

        this.outboundLoadForm.controls.carrierName.setValidators(control => {
            const currentCarrier = this.carriers?.find(c => c.id === this.outboundLoadForm.controls.carrierId.value);
            return (currentCarrier?.carrierCode !== 'NOSC' && currentCarrier?.carrierCode !== 'CUPU') || control.value
                ? null
                : { requiredIfNosc: true };
        });

        this.outboundLoadForm.controls.customerReferenceNumber.setAsyncValidators(control => {
            if (!this.outboundLoadForm.controls.customerId.value || !control.value)
                return Promise.resolve(null);

            return this.outboundLoadService.refNumberInUse(this.outboundLoadForm.controls.customerId.value, control.value)
                .pipe(map(inUse => {
                    if (!!inUse && (!this.existingLoadId || this.existingLoadId !== inUse))
                        return { numberInUse: { valid: false } };
                    else
                        return null;
                }));
        });

        this.olDefaultValuefields = { productId: null, quantity: 0, isScanned: false };

        const carrierFinished = new Subject<void>();
        const contextFinished = new Subject<void>();

        this.subscriptions = [
            // this subscription should stay first
            this.startupSubscription = zip(carrierFinished,
                contextFinished,
                this.productService.CurrentProducts.pipe(tap(products => {
                    this.products = products;
                })))
            .pipe(
                this.activityService.Wait('Outbound Loads Form'),
                take(1),
                switchMap(_ => this.initializeForm())
            ).subscribe(_ => {
                this.autoSaveEnabled = true;
            }),

            this.shiftContextService.CurrentContext.subscribe(context => {
                const oldSite = this.context?.site;
                this.context = context;

                if (oldSite?.id !== this.context?.site?.id)
                    this.outboundLoadService.getNextBolNumber(context.site.id).subscribe(bol => {
                        this.outboundLoadForm.controls.propakBOLNum.setValue(bol);
                    });

                this.siteSubinventories = context.site.subinventories
                    .filter(s => s.isActive)
                    .map(si => ({ value: si.id, label: si.name }));

                if (this.siteSubinventories.length && !this.siteSubinventories.find(s => s.value === this.outboundLoadForm.controls.siteSubinventoryId.value))
                    this.outboundLoadForm.controls.siteSubinventoryId.setValue(null);

                contextFinished.next();
            }),

            this.carrierService.getCarriers().subscribe(carriers => {
                this.setCarriers(carriers);
                carrierFinished.next();
            }),

            fromFormControl(this.outboundLoadForm.controls.loadShiftId)
                .pipe(
                    filter(sId => !!sId),
                    switchMap(sId => this.workedShiftService.PeekById(sId)),
                    switchMap(loadedShift => this.customerService.getCustomers(loadedShift.siteId, loadedShift.date, 'outbound')))
                .subscribe(customerAssignments => {
                    if (customerAssignments)
                        this.customerAssignments.next(customerAssignments);
                }),

            this.shiftContextService.SiteConfig.subscribe(configs => {
                this.siteTimezone = configs
                    .find(c => c.configKey === SiteConfig.SITE_TIME_ZONE)
                    ?.GetValue<string>()
                    ?? environment.fallbackTimeZone;
            }),

            this.outboundLoadForm.valueChanges.subscribe(_value => {
                this.autoSaveServiceSetItem();
            }),

            combineLatest([
                this.shiftContextService.CurrentContext,
                fromFormControl(this.outboundLoadForm.controls.loadShiftId).pipe(
                    filter(sId => !!sId),
                    switchMap(sId => this.workedShiftService.PeekById(sId)))
            ]).subscribe(([scs, loadedShift]) => {
                this.customerService.getCustomers(scs.site.id, loadedShift.date, 'outbound')
                    .subscribe(c => this.customerAssignments.next(c));
            }),

            this.outboundLoadForm.controls.customerId.valueChanges.subscribe(value => {
                const customer = this.customerAssignments.value.customers.find(s => s.id === value);
                if (customer)
                    this.cfGlidRequired = customer.accountNumber === environment.ids.ChepCustomerAccountNumber;

                this.handleCustomerReferenceRequired();
            }),

            this.outboundLoadForm.controls.carrierId.valueChanges.subscribe(id => {
                const currentCarrier = this.carriers?.find(c => c.id === id);
                this.showCarrierName = currentCarrier?.carrierCode === 'NOSC' || currentCarrier?.carrierCode === 'CUPU';
                this.outboundLoadForm.controls.carrierName.updateValueAndValidity();
            }),

            combineLatest([
                this.outboundLoadForm.controls.loadTime.valueChanges,
                this.outboundLoadForm.controls.timeShipped.valueChanges
            ]).subscribe(([_lt, _ts]) => {
                if (this.outboundLoadForm.controls.loadType.value === OutboundLoadStatus.Shipped) {
                    this.outboundLoadForm.controls.shippedShiftId.updateValueAndValidity();
                    this.outboundLoadForm.controls.loadShiftId.updateValueAndValidity();
                }
            })
        ];
    }

    setOutboundLoadFormValues(load: OutboundLoad): void {
        this.existingLoadId = load.id;

        const values = {
            loadType: load.outboundLoadStatusId ?? null,
            siteSubinventoryId: load.siteSubinventoryId ?? null,
            trailerId: load.trailerId ?? null,
            sealNumber: load.sealNumber ?? '',
            loadDate: load.loadedTimestamp?.startOf('day').toJSDate() ?? null,
            loadTime: load.loadedTimestamp ? timeFormat(load.loadedTimestamp?.setZone(this.siteTimezone)) : null,
            loadShiftId: load.loadedWorkedShiftId ?? null,
            carrierId: load.carrierId ?? null,
            poolerId: load.poolerId ?? null,
            carrierName: load.carrierName ?? '',
            customerId: load.customerId ?? null,
            shipToLocation: load.customerSiteId ?? null,
            customerReferenceNumber: load.customerReferenceNumber ?? null,
            altReferenceNumber: load.altReferenceNumber ?? null,
            propakBOLNum: load.propakBOLNumber,
            isReuse: !!load.isReuse,
            comments: load.comments ?? '',
            resendNotification: false,
            customerNeededDate: null,
            customerNeededTime: '',
            scheduledPickupDate: null,
            scheduledPickupTime: '',
            dateShipped: null,
            timeShipped: null,
            shippedShiftId: null,
            isRejected: false,
            rejectionReason: ''
        };

        this.showCarrierName = !this.outboundLoadForm.controls.carrierId.value;

        if (load.customerNeededTimestamp && load.customerNeededTimestamp.isValid) {
            values.customerNeededDate = load.customerNeededTimestamp.startOf('day').toJSDate();
            values.customerNeededTime = load.customerNeededTimestamp.toFormat('H:mm');
        }

        if (load.scheduledPickupTimestamp && load.scheduledPickupTimestamp.isValid) {
            values.scheduledPickupDate = load.scheduledPickupTimestamp.startOf('day').toJSDate();
            values.scheduledPickupTime = load.scheduledPickupTimestamp.toFormat('H:mm');
        }

        if (values.loadType ===  OutboundLoadStatus.Shipped) {
            values.dateShipped = load.shippedTimestamp?.startOf('day').toJSDate() ?? null;
            values.timeShipped = load.shippedTimestamp ? timeFormat(load.shippedTimestamp?.setZone(this.siteTimezone)) : null;
            values.shippedShiftId = load.shippedWorkedShiftId;
            values.isRejected = !!load.isRejected;
            values.rejectionReason = load.rejectionReason ?? '';
        }

        this.outboundLoadLineItems = (load.outboundLoadLineItems ?? []).map(li => ({
            id: li.id,
            productId: li.productId,
            quantity: li.quantity,
            isScanned: !!li.isScanned,
            isUnderload: !!li.isUnderload
        }));

        this.exceptionItems = (load.outboundLoadExceptions ?? []).map(exception => ({
            id: exception.id,
            description: exception.description,
            outboundLoadExceptionType: exception.outboundLoadExceptionType
        }));

        this.documents = load.documentIds ?? [];

        this.outboundLoadForm.setValue(values);
        this.outboundLoadForm.updateValueAndValidity({ onlySelf: false, emitEvent: true });

        this.showAltRefNum = (!!this.outboundLoadLineItems.length) && this.outboundLoadLineItems.some(i => i.isUnderload);

        if (load.firstClosedShift) {
            this.outboundLoadForm.disable();
            this.outboundLoadLineForm.disable();
            this.closedWarning = `The load cannot be edited because ${
                load.firstClosedShift.date.toLocaleString()
            } shift ${
                load.firstClosedShift.shift.number
            } is closed.`;
        }
        else if (load.whiteWoodLocked) {
            this.outboundLoadForm.disable();
            this.outboundLoadLineForm.disable();
            this.closedWarning = `This White Wood load has been locked from editing.`;
        }
        else {
            this.outboundLoadForm.enable();
            this.outboundLoadLineForm.enable();
        }

        this.loadedNotificationTS = load.loadedNotificationTS;
        this.stockTransfer = load.stockTransfer;
        this.handleLoadTypeChange(this.outboundLoadForm.controls.loadType.value);
    }

    initializeForm(): Observable<any> {
        const autoSaved = this.autoSaveService.GetItem<OutboundLoad>(this.pageName, this.formDataName);

        if (this.route.snapshot.paramMap.has('id')) {
            const id = parseInt(this.route.snapshot.paramMap.get('id'), 10);
            return this.outboundLoadService.getLoadDetail(id).pipe(tap(load => {
                this.setOutboundLoadFormValues(load);
            }));
        } else if (autoSaved) {
            // this.existingLoadId = null;
            this.setOutboundLoadFormValues(this.outboundLoadService.parseOutboundLoadDates(autoSaved));
            this.outboundLoadForm.markAllAsTouched();
            this.messageService.add(this.autoSaveService.DataRestoredMsg);
            return of(true);
        } else {
            this.existingLoadId = null;
            this.outboundLoadForm.controls.loadType.setValue(OutboundLoadStatus.Shipped);
            this.handleLoadTypeChange( OutboundLoadStatus.Shipped);
            const jsDate = this.context.date.toJSDate();
            this.outboundLoadForm.controls.customerNeededDate.setValue(jsDate);
            this.outboundLoadForm.controls.scheduledPickupDate.setValue(jsDate);
            this.outboundLoadForm.controls.loadShiftId.setValue(this.context.id);
            this.outboundLoadForm.controls.shippedShiftId.setValue(this.context.id);
            return of(true);
        }
    }

    onChangeLoadType(): void {
        this.handleLoadTypeChange(this.outboundLoadForm.controls.loadType.value);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        const outboundLoadForm = this.autoSaveService.GetItem<OutboundLoad>(this.pageName, this.formDataName);
        if (!this.route.snapshot.paramMap.has('id') && outboundLoadForm) {
            this.messageService.add(this.autoSaveService.DataSavedMsg);
        }
    }

    onLoadedShiftDateChanged(date: DateTime): void {
        if (date)
            this.outboundLoadForm.controls.loadDate.setValue(date.toJSDate());
    }

    onShippedShiftDateChanged(date: DateTime): void {
        if (date)
            this.outboundLoadForm.controls.dateShipped.setValue(date.toJSDate());
    }

    setCarriers(carriers: Carrier[]): void {
        this.carriers = carriers.slice().sort((l, r) => {
            const left = l.carrierCode === 'NOSC' || l.carrierCode === 'CUPU' ? '' : l.carrierCodeName;
            const right = r.carrierCode === 'NOSC' || r.carrierCode === 'CUPU' ? '' : r.carrierCodeName;
            return left.localeCompare(right);
        });

        if (this.carriers.length === 1)
            this.outboundLoadForm.controls.carrierId.setValue(this.carriers[0].id);
        else if (this.outboundLoadForm.controls.carrierId.value
                && !this.carriers.find(c => c.id === this.outboundLoadForm.controls.carrierId.value))
            this.outboundLoadForm.controls.carrierId.setValue(null);

        this.carrierSelects = this.carriers.map(c => ({ value: c.id, label: c.carrierCodeName }));
    }

    showDropdown(_e: any): void {
        if (!this.entryFocus) {
            this.poolerSelect.show();
            this.entryFocus = true;
        }
        else {
            // set to false due to dropdown focus interaction activating dropdown loop.
            this.entryFocus = false;
        }
    }

    hideDropdown(_e: any): void {
        if (this.entryFocus) {
            this.poolerSelect.hide();
        }
    }

    showDialogToAddCarrier(): void {
        this.displayDialog = true;
    }

    closeAddCarrierDialog(dialogClosed: boolean): void {
        this.displayDialog = dialogClosed;
    }

    carrierSelectedChange(newCarrier: Carrier): void {
        this.setCarriers([...this.carriers, newCarrier]);

        this.outboundLoadForm.controls.carrierId.setValue(newCarrier.id);
    }

    customerSiteChanged(site: CustomerSite): void {
        this.outboundLoadForm.controls.poolerId.setValue(site.customerId);

        const pooler = this.customerAssignments.value.customers.find(p => p.id === site.customerId);
        // NOTE(nicholas-ramsey): The `site` may be a pre-existing site selected from the duplicate list
        pooler.customerSites = [...pooler.customerSites.filter(s => s.id !== site.id), site];
        this.customerAssignments.next(this.customerAssignments.value);

        this.outboundLoadForm.controls.shipToLocation.setValue(site.id);
    }

    onChangeProduct(e: any): void {
        this.outboundLoadLineForm.controls.productId.setValue(e.value);
        if (this.outboundLoadLineForm.controls.productId.value !== null) {
            const product = this.products.find(p => p.id === this.outboundLoadLineForm.get('productId').value);
            this.newIGPS = this.isIGPS(product.id);
            this.outboundLoadForm.controls.shipToLocation.updateValueAndValidity();
            this.outboundLoadLineForm.controls.quantity.setValidators
            ([Validators.required, Validators.min(1)]);
            if (!this.outboundLoadForm.controls.isReuse.value)
                this.outboundLoadLineForm.controls.quantity.setValidators([
                    Validators.max(product.maxLoadQuantity), this.outboundLoadLineForm.get('quantity').validator]);

        }
    }

    // begin outbound load line item grid methods
    onChangeOutboundLoadField(outboundLoadLineItem: OutboundLoadLineItem): void {
        if (String(outboundLoadLineItem.quantity) === '') {
            this.messageService.add({ severity: 'warn', summary: 'Could not Update Line Item', detail: 'Fields are required.' });
        } else {
            const product = this.products.find(p => p.id === outboundLoadLineItem.productId);
            if (outboundLoadLineItem.quantity >= 1 &&
              (this.outboundLoadForm.controls.isReuse.value || outboundLoadLineItem.quantity <= product.maxLoadQuantity)) {
                this.messageService.add({
                    severity: 'success', summary: 'Line Item Updated',
                    detail: 'Save the load to complete changes.'
                });
                this.autoSaveServiceSetItem();
            } else {
                if (+outboundLoadLineItem.quantity === 0) {
                    this.messageService.add({ severity: 'warn', summary: 'Could not Create Line Item',
                     detail: 'Quantity must not be zero.' });
                } else {
                    this.messageService.add({ severity: 'warn', summary: 'Could not Create Line Item',
                    detail: `Max quantity for item exceeded.\n Quantity should be less than ${product.maxLoadQuantity}.` });
                }
            }
        }

        this.showAltRefNum = (!!this.outboundLoadLineItems.length) && this.outboundLoadLineItems.some(i => i.isUnderload);
    }

    saveOLLI(): void {
        if (!this.outboundLoadLineForm.valid) {
            this.outboundLoadLineForm.markAllAsTouched();
            this.messageService.add(this._outboundLoadLineItemNotCreatedMsg);
        } else {
            const product = this.products.find(p => p.id === this.outboundLoadLineForm.get('productId').value);
            if (this.outboundLoadLineForm.controls.quantity.value < 1)
                this.messageService.add({ severity: 'warn', summary: 'Could not Create Line Item',
                     detail: 'Quantity must not be zero.' });
            else if (product.maxLoadQuantity
                        && (!this.outboundLoadForm.controls.isReuse.value && this.outboundLoadLineForm.controls.quantity.value >
                        product.maxLoadQuantity) )
                this.messageService.add({ severity: 'warn', summary: 'Could not Create Line Item',
                    detail: `Max quantity for item exceeded.\n Quantity should be less than ${product.maxLoadQuantity}.` });
            else {
                const entry: OutboundLoadLineItem = {
                    id: null,
                    productId: this.outboundLoadLineForm.controls.productId.value,
                    quantity: this.outboundLoadLineForm.controls.quantity.value,
                    isScanned: this.outboundLoadLineForm.controls.isScanned.value,
                    isUnderload: this.outboundLoadLineForm.controls.isUnderload.value
                };
                this.outboundLoadLineItems = [ ...this.outboundLoadLineItems, entry ];
                this.outboundLoadLineItem = null;
                this.outboundLoadLineItem = { id: null, productId: null, quantity: 0, isScanned: false, isUnderload: false };
                this.newIGPS = false;
                this.outboundLoadLineForm.reset(this.olDefaultValuefields);
                this.messageService.add(this._outboundLoadLineItemCreatedMsg);
                this.autoSaveServiceSetItem();

                this.showAltRefNum = (!!this.outboundLoadLineItems.length) && this.outboundLoadLineItems.some(i => i.isUnderload);
            }

            this.outboundLoadForm.controls.shipToLocation.updateValueAndValidity();
            this.outboundLoadForm.updateValueAndValidity();
            this.productSelect.applyFocus();
        }
    }

    onOutboundLoadRowCancel(outboundLoadLineItem: OutboundLoadLineItem): void {
        const index = this.outboundLoadLineItems.indexOf(outboundLoadLineItem);
        this.outboundLoadLineItems = this.outboundLoadLineItems.filter((_val, i) => i !== index);
        this.outboundLoadLineItem = null;
        this.outboundLoadLineItem = { id: null, productId: null, quantity: 0, isScanned: false, isUnderload: false };
        this.messageService.add(this._outboundLoadLineItemDeletedMsg);
        this.autoSaveServiceSetItem();

        this.outboundLoadForm.updateValueAndValidity();

        this.showAltRefNum = (!!this.outboundLoadLineItems.length) && this.outboundLoadLineItems.some(i => i.isUnderload);
    }

    // end outbound load line item grid methods

    onChangeCustomer(customerId: number): void {
        const isOwnPooler = this.customerAssignments.value.assignments
            .some(a => a.siteSubinventoryId === this.outboundLoadForm.controls.siteSubinventoryId.value
                && a.customerId === customerId
                && a.poolerId === customerId);

        if (isOwnPooler) {
            this.outboundLoadForm.controls.poolerId.setValue(customerId);
        }
    }

    showDialogToAddShipTo(): void {
        this.displayAddShipToDialog = true;
    }

    closeAddShippedFromDialog(dialogClosed: boolean): void {
        this.displayAddShipToDialog = dialogClosed;
    }

    // begin exception item grid methods
    onChangeExceptionField(exceptionItem: OutboundLoadException): void {
        if (exceptionItem.description === '') {
            this.messageService.add({ severity: 'warn', summary: 'Could not Update Line Item', detail: 'Fields are required.' });
        } else {
            this.messageService.add({ severity: 'success', summary: 'Exception Updated', detail: 'Save the load to complete changes.' });
            this.autoSaveServiceSetItem();
        }
    }

    saveEI(): void {
        if (!this.exceptionForm.valid) {
            this.exceptionForm.markAllAsTouched();
            this.messageService.add(this._exceptionItemNotCreatedMsg);
        } else {
            const entry: OutboundLoadException = {
                id: null,
                ...this.exceptionForm.value
            };
            this.exceptionItems = [ ...this.exceptionItems, entry ];
            this.exceptionItem = null;
            this.exceptionItem = { id: null, description: '' };
            this.exceptionForm.reset(this.eDefaultValuefields);
            this.messageService.add(this._exceptionItemCreatedMsg);
        }
        this.autoSaveServiceSetItem();
    }

    onExceptionRowCancel(exceptionItem: OutboundLoadException): void {
        const index = this.exceptionItems.indexOf(exceptionItem);
        this.exceptionItems = this.exceptionItems.filter((_val, i) => i !== index);
        this.exceptionItem = null;
        this.exceptionItem = { id: null, description: '' };
        this.messageService.add(this._exceptionItemDeletedMsg);
        this.autoSaveServiceSetItem();
    }
    // end exception item grid methods


    setOutboundLoadModelData(): OutboundLoad {
        const formData: OutboundLoad = {
            id: this.existingLoadId ?? 0,
            siteId: this.context?.site ? this.context?.site.id : null,
            siteSubinventoryId: this.outboundLoadForm.controls.siteSubinventoryId.value,
            loadedWorkedShiftId: this.outboundLoadForm.controls.loadShiftId.value,
            loadedTimestamp: toTimestamp(this.outboundLoadForm.controls.loadDate.value, this.outboundLoadForm.controls.loadTime.value, this.siteTimezone),
            propakBOLNumber: this.outboundLoadForm.controls.propakBOLNum.value,
            trailerId: this.outboundLoadForm.controls.trailerId.value,
            carrierId: this.outboundLoadForm.controls.carrierId.value,
            sealNumber: this.outboundLoadForm.controls.sealNumber.value,
            outboundLoadStatusId: this.outboundLoadForm.controls.loadType.value,
            customerNeededTimestamp: toTimestamp(this.outboundLoadForm.controls.customerNeededDate.value, this.outboundLoadForm.controls.customerNeededTime.value, this.siteTimezone),
            scheduledPickupTimestamp: toTimestamp(this.outboundLoadForm.controls.scheduledPickupDate.value, this.outboundLoadForm.controls.scheduledPickupTime.value, this.siteTimezone),
            customerReferenceNumber: this.outboundLoadForm.controls.customerReferenceNumber.value?.trim(),
            altReferenceNumber: this.outboundLoadForm.controls.altReferenceNumber.value?.trim() ?? null,
            stockTransfer: this.stockTransfer,
            isReuse: !!this.outboundLoadForm.controls.isReuse.value,
            customerId: this.outboundLoadForm.controls.customerId.value,
            poolerId: this.outboundLoadForm.controls.poolerId.value,
            customerSiteId: this.outboundLoadForm.controls.shipToLocation.value,
            comments: this.outboundLoadForm.controls.comments.value,

            outboundLoadLineItems: (this.outboundLoadLineItems ?? []).map(li => ({
                id: li.id ?? 0,
                productId: li.productId,
                quantity: li.quantity,
                isScanned: !!li.isScanned,
                isUnderload: !!li.isUnderload
            })),
            outboundLoadExceptions: [],

            // set below

            carrierName: '',
            shippedWorkedShiftId: null,
            shippedTimestamp: null,
            canceledWorkedShiftId: null,
            isRejected: false,
            rejectionReason: null
        };

        const currentCarrier = this.carriers?.find(c => c.id === this.outboundLoadForm.controls.carrierId.value);

        if (currentCarrier?.carrierCode === 'NOSC' || currentCarrier?.carrierCode === 'CUPU')
            formData.carrierName = this.outboundLoadForm.controls.carrierName.value;

        if (formData.outboundLoadStatusId === OutboundLoadStatus.Shipped) {
            formData.shippedTimestamp = toTimestamp(this.outboundLoadForm.controls.dateShipped.value, this.outboundLoadForm.controls.timeShipped.value, this.siteTimezone);
            formData.shippedWorkedShiftId = this.outboundLoadForm.controls.shippedShiftId.value;
            formData.isRejected = !!this.outboundLoadForm.controls.isRejected.value;
            formData.rejectionReason = this.outboundLoadForm.controls.rejectionReason.value;
        }

        formData.outboundLoadExceptions = (this.exceptionItems || []).map(exception => ({
            id: exception.id,
            description: exception.description,
            outboundLoadExceptionType: exception.outboundLoadExceptionType
        }));
        return formData;
    }

    saveLoad(print: boolean = false): void {
        if (!this.outboundLoadForm.valid) {
            this.outboundLoadForm.markAllAsTouched();

            console.log('invalid form', this.outboundLoadForm.value);

            for(const control in this.outboundLoadForm.controls) {
                const c = this.outboundLoadForm.controls[control];
                console.log(control + ' errors', c.valid, c.errors);
            }

            this.messageService.add({ severity: 'warn', summary: 'Could not Create Load', detail: 'Please correct the listed errors.' });
            return;
        }

        if (this.invalidProduct) {
            this.messageService.add({ severity: 'warn', summary: 'Could not Update Load', detail: 'Please select a new product for this load or contact an Admin.' });
            return;
        }

        this.saving = true;

        const formData = this.setOutboundLoadModelData();

        let observable: Observable<{ id: number, message: Message, load: OutboundLoad}>;

        if (this.existingLoadId) {
            formData.id = this.existingLoadId;
            observable = this.updateOutboundLoad(formData);
        } else {
            observable = this.createOutboundLoad(formData);
        }

        observable
            .pipe(this.activityService.Wait('saving outbound load'))
            .subscribe({
                next: result => {
                    this.messageService.add(result.message);

                    if (this.outboundLoadForm.controls.resendNotification.value)
                        this.outboundLoadService.resendNotification(result.load.id).subscribe();

                    if (this.outboundLoadForm.controls.resendNotification.value ||
                    (result.load.loadedNotificationTS &&
                        this.loadedNotificationTS &&
                        !result.load.loadedNotificationTS.equals(this.loadedNotificationTS)))
                        this.messageService.add({
                            severity: 'success',
                            life: 10000,
                            summary: 'Notification for this load has been sent',
                            detail: ''});
                    this.router.navigateByUrl('/loads/outbound');
                    this.saving = false;
                    this.autoSaveService.RemoveItem(this.pageName, this.formDataName);

                    if (print) {
                        this.bolService.DownloadBolPdf(result.id);
                    }
                },
                error: _ => {
                    this.saving = false;
                }
            });
    }

    createOutboundLoad(formData: OutboundLoad): Observable<{ id: number, message: Message, load: OutboundLoad}> {
        return this.outboundLoadService.createOutboundLoad(formData).pipe(map(load => ({
            id: load.id,
            message: {
                severity: 'success',
                summary: 'Load Created',
                detail: `Load ${this.outboundLoadForm.controls.propakBOLNum.value} has been saved.`
            },
            load
        })));
    };


    updateOutboundLoad(formData: OutboundLoad): Observable<{ id: number, message: Message, load: OutboundLoad}> {
        formData.id = this.existingLoadId;

        return this.outboundLoadService.updateOutboundLoad(formData).pipe(map(load => ({
            id: load.id,
            message: {
                severity: 'success',
                summary: 'Load Updated',
                detail: `Load ${this.outboundLoadForm.controls.propakBOLNum.value} has been saved.`
            },
            load
         })));
    }

    autoSaveServiceSetItem(): void {
        if (!this.route.snapshot.paramMap.has('id') && this.autoSaveEnabled) {
            const data = this.setOutboundLoadModelData();
            this.autoSaveService.SetItem(this.pageName, this.formDataName, data);
        }
    }

    resetOutboundLoadAutoSave(): void {
        this.autoSaveService.RemoveItem(this.pageName, this.formDataName);
        this.initializeForm().subscribe(() => {
            this.messageService.add(this.autoSaveService.AutoSavedDataRemovedMsg);
        });
    }

    isIGPS(productId: number): boolean {
        const product = this.products.find(p => p.id === productId);

        return product?.customer?.includes('IGPS') ?? false;
    }

    shipToLocationSelected(): void {
        const shipTo = this.getPoolerSite();
        this.stockTransfer = shipTo?.isStockTransferSite ?? false;
    }

    getTotal(): number {
         return this.outboundLoadLineItems.length > 0 ? this.outboundLoadLineItems.map(olli => olli.quantity).reduce((a, b) => +a + +b) : 0;
    }

    tpmSelected(): boolean {
        return this.context?.site?.subinventories
            .find(ss => ss.id === this.outboundLoadForm.controls.siteSubinventoryId.value)
            ?.subinventoryType === SubinventoryType.TpmProduction;
    }

    onChangeReuse(): void {
        const product = this.products.find(p => p.id === this.outboundLoadLineForm.get('productId').value);
        if (product) {
            this.outboundLoadLineForm.controls.quantity.clearValidators();
            if (this.outboundLoadForm.controls.isReuse.value) {
                this.outboundLoadLineForm.controls.quantity.setValidators
                ([Validators.required, Validators.min(1)]);
            } else {
                this.outboundLoadLineForm.controls.quantity.setValidators
                ([Validators.max(product.maxLoadQuantity), Validators.required, Validators.min(1)]);
            }
            this.outboundLoadLineForm.controls.quantity.updateValueAndValidity();
        }
    }

    validateQuantityMessage(): { [key: string]: string } {
        const required = 'Quantity is required.';
        const max = 'Quantity is greater than allowed for product';

        return this.outboundLoadForm.controls.isReuse.value ? { required } : { required, max };
    }

    getShipToAddress(): string {
        const shipTo = this.getPoolerSite();

        if (!shipTo)
            return 'Select a Ship From';

        // this is the validator
        if (!shipTo.address1)
            return 'No Address';

        return `${shipTo.address1} ${shipTo.city}, ${shipTo.state} ${shipTo.postalCode}`;
    }

    handleProductHide(index?: number): void {
        if (!index) {
            this.productSelect.applyFocus();
        }
    }

    checkValue($event: any) {
        if ($event === null && !this.invalidProduct) {
            this.messageService.add({ severity: 'error', sticky: true, summary: 'Previously selected Product no longer valid', detail: 'Please select a new product for this load or contact an Admin.' });
            this.invalidProduct = true;
        }
        else {
            this.invalidProduct = this.productsDropdown.some(p => p.value === null);
        }
    }
}
